import React, { useEffect, useState } from 'react';
import { Upload, Check, X, Ban, Eye, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { StepUpDialog } from './StepUpDialog';
import { useAuth } from '../context/AuthContext';

const STATUS = { pending: 'text-yellow-400', published: 'text-green-400', rejected: 'text-red-400', superseded: 'text-gray-500', cancelled: 'text-gray-500' };
const err = (e) => toast.error(e?.response?.data?.detail || 'Operación rechazada');
const inputCls = 'mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm';

const UploadForm = ({ types, onDone }) => {
  const [f, setF] = useState({ doc_type: 'terminos_usuarios', version: '', title: '', summary: '', content_md: '' });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.md') || file.size > 300000) { toast.error('Sube un archivo .md de hasta 300 KB'); return; }
    setF((p) => ({ ...p, content_md: '' }));
    const text = await file.text();
    setF((p) => ({ ...p, content_md: text }));
  };
  const submit = async () => {
    try {
      const r = await api.post('/admin/legal/documents', { ...f, title: f.title || types[f.doc_type]?.title, summary: f.summary || null });
      toast.success(r.data.message);
      setF((p) => ({ ...p, version: '', summary: '', content_md: '' }));
      onDone();
    } catch (e) { err(e); }
  };
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3" data-testid="legal-upload-form">
      <h3 className="text-white font-semibold">Subir nueva versión</h3>
      <div className="grid md:grid-cols-3 gap-3">
        <label className="text-xs text-gray-400">Documento<select value={f.doc_type} onChange={set('doc_type')} className={inputCls} data-testid="legal-upload-type">
          {Object.entries(types).map(([k, t]) => <option key={k} value={k}>{t.title}</option>)}</select></label>
        <label className="text-xs text-gray-400">Versión (p. ej. 1.1)<input value={f.version} onChange={set('version')} className={inputCls} data-testid="legal-upload-version" /></label>
        <label className="text-xs text-gray-400">Título<input value={f.title} onChange={set('title')} placeholder={types[f.doc_type]?.title} className={inputCls} data-testid="legal-upload-title" /></label>
      </div>
      <label className="block text-xs text-gray-400">Resumen de cambios<input value={f.summary} onChange={set('summary')} className={inputCls} data-testid="legal-upload-summary" /></label>
      <label className="block text-xs text-gray-400">Archivo Markdown (.md)<input type="file" accept=".md,text/markdown" onChange={onFile} className="mt-1 block text-sm text-gray-300" data-testid="legal-upload-file" /></label>
      <textarea rows={6} value={f.content_md} onChange={set('content_md')} placeholder="…o pega aquí el contenido Markdown" className={`${inputCls} font-mono`} data-testid="legal-upload-content" />
      <Button onClick={submit} disabled={!/^\d{1,3}\.\d{1,3}$/.test(f.version) || f.content_md.length < 200} className="bg-[#FF6B35] hover:bg-[#e55a2a]" data-testid="legal-upload-submit">
        <Upload className="w-4 h-4 mr-1" /> Enviar para aprobación
      </Button>
    </div>
  );
};

export const LegalDocsPanel = () => {
  const { can, user } = useAuth();
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = () => api.get('/admin/legal/documents').then((r) => setData(r.data)).catch(err);
  useEffect(() => { load(); }, []);

  const submit = async (x) => {
    try {
      if (dialog.kind === 'approve') await api.post(`/admin/legal/documents/${dialog.id}/approve`, { totp_code: x.totp_code, reason: x.reason || null });
      else await api.post(`/admin/legal/documents/${dialog.id}/reject`, { reason: x.reason });
      toast.success(dialog.kind === 'approve' ? 'Publicado en el CDN' : 'Versión rechazada');
      load();
      return true;
    } catch (e) { err(e); return false; }
  };
  const show = (id) => api.get(`/admin/legal/documents/${id}`).then((r) => setPreview(r.data)).catch(err);
  const cancel = (id) => api.post(`/admin/legal/documents/${id}/cancel`).then(() => { toast.success('Cancelada'); load(); }).catch(err);
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="legal-docs-panel">
      {!data.cdn_configured && <p className="text-yellow-400 text-sm">El bucket de archivos legales no está configurado: no se puede publicar.</p>}
      {can('legal.upload') && <UploadForm types={data.types} onDone={load} />}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-x-auto">
        <div className="flex justify-between items-center px-4 py-3"><h3 className="text-white font-semibold">Versiones</h3>
          <Button variant="ghost" size="sm" onClick={load} data-testid="legal-refresh"><RefreshCw className="w-4 h-4" /></Button></div>
        <table className="w-full text-sm">
          <thead className="bg-gray-800 text-xs text-gray-400 uppercase"><tr>
            <th className="px-3 py-2 text-left">Documento</th><th className="px-3 py-2 text-left">Versión</th><th className="px-3 py-2 text-left">Estado</th>
            <th className="px-3 py-2 text-left">Subió / decidió</th><th className="px-3 py-2 text-left">Aceptaciones</th><th className="px-3 py-2 text-right">Acciones</th></tr></thead>
          <tbody className="divide-y divide-gray-800">
            {data.documents.map((d) => (
              <tr key={d.id} data-testid={`legal-doc-${d.id}`}>
                <td className="px-3 py-2 text-gray-200">{data.types[d.doc_type]?.title}<span className="block text-xs text-gray-500">{d.summary}</span></td>
                <td className="px-3 py-2 text-gray-300">{d.version}</td>
                <td className={`px-3 py-2 ${STATUS[d.status]}`} data-testid={`legal-doc-status-${d.id}`}>{d.status}
                  {d.public_url && <a href={d.public_url} target="_blank" rel="noreferrer" className="block text-xs text-[#FF6B35] break-all">{d.object_key}</a>}</td>
                <td className="px-3 py-2 text-xs text-gray-400">{d.uploaded_by_email}<br />{d.decided_by_email}{d.decision_reason ? ` · ${d.decision_reason}` : ''}</td>
                <td className="px-3 py-2 text-gray-300">{d.acceptances}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => show(d.id)} data-testid={`legal-preview-${d.id}`}><Eye className="w-4 h-4" /></Button>
                  {d.status === 'pending' && can('legal.approve') && <>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setDialog({ kind: 'approve', id: d.id })} data-testid={`legal-approve-${d.id}`}><Check className="w-4 h-4" /></Button>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setDialog({ kind: 'reject', id: d.id })} data-testid={`legal-reject-${d.id}`}><X className="w-4 h-4" /></Button></>}
                  {d.status === 'pending' && d.uploaded_by === user?.id && <Button size="sm" variant="ghost" onClick={() => cancel(d.id)} data-testid={`legal-cancel-${d.id}`}><Ban className="w-4 h-4" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preview && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="legal-preview">
          <div className="flex justify-between"><h3 className="text-white font-semibold">{preview.title} v{preview.version}</h3>
            <Button size="sm" variant="ghost" onClick={() => setPreview(null)}><X className="w-4 h-4" /></Button></div>
          <p className="text-xs text-gray-500">SHA-256 {preview.sha256}</p>
          <pre className="mt-3 max-h-96 overflow-auto text-xs text-gray-300 whitespace-pre-wrap">{preview.content_md}</pre>
        </div>
      )}
      <StepUpDialog open={!!dialog} title={dialog?.kind === 'approve' ? 'Aprobar y publicar en files.yooin.com.pe' : 'Rechazar versión'}
        needTotp={dialog?.kind === 'approve'} needReason={dialog?.kind === 'reject'} onSubmit={submit} onClose={() => setDialog(null)} />
    </div>
  );
};

export default LegalDocsPanel;
